import { useState } from 'react'
import { performanceService } from '@/services/api/performanceService'
import toast from 'react-hot-toast'
import type { KPI, PendingObjective } from '@/components/performance/manager/objective-config/types'

interface UseObjectiveEditingOptions {
  teamMember: { employee_id: string } | null
  userRole: 'manager' | 'employee'
  activeTab: string
  effectiveYear: number
  onUpdate: (objectiveId: number, updates: PendingObjective, employeeId?: string) => Promise<void>
  onObjectivesChange?: () => Promise<void>
  setFullTeamMember: (member: any) => void
  setCollaborativeSession: (session: any) => void
  setIsRefreshing: (refreshing: boolean) => void
}

export function useObjectiveEditing({
  teamMember,
  userRole,
  activeTab,
  effectiveYear,
  onUpdate,
  onObjectivesChange,
  setFullTeamMember,
  setCollaborativeSession,
  setIsRefreshing
}: UseObjectiveEditingOptions) {
  const [editingObjective, setEditingObjective] = useState<number | null>(null)
  const [editedObjectives, setEditedObjectives] = useState<Record<number, PendingObjective>>({})
  // The objective as it was when editing started, to tell a real edit from an
  // unchanged save (HR UAT list #9: saving unchanged forced a re-approval).
  const [originalObjectives, setOriginalObjectives] = useState<Record<number, string>>({})
  const [savingEdit, setSavingEdit] = useState<number | null>(null)

  const getCurrentObjective = (objective: PendingObjective): PendingObjective => {
    return editedObjectives[objective.id] || objective
  }

  const inferMeasurementMode = (kpi: KPI): KPI['measurement_mode'] => {
    if (kpi.measurement_mode) return kpi.measurement_mode

    const unit = (kpi.unit || '').toLowerCase().trim()

    if (unit === '%' || unit === 'percent' || unit === 'percentage') {
      return 'percentage'
    }
    if (unit === 'status' || unit === 'rag' || unit === 'rating') {
      return 'rating'
    }
    if (unit === 'achieved' || unit === 'yes/no' || unit === 'complete') {
      return 'binary'
    }

    return 'numeric'
  }

  const inferMeasurementDirection = (kpi: KPI): KPI['measurement_direction'] => {
    if (kpi.measurement_direction) return kpi.measurement_direction

    const unit = (kpi.unit || '').toLowerCase()
    const name = (kpi.name || '').toLowerCase()

    if (unit.includes('day') || unit.includes('hour') || unit.includes('minute') ||
        name.includes('turnaround') || name.includes('response time') ||
        name.includes('waiting') || name.includes('defect') || name.includes('error')) {
      return 'lower_is_better'
    }

    return 'higher_is_better'
  }

  const handleStartEdit = (objective: PendingObjective) => {
    if (import.meta.env.DEV) console.log('handleStartEdit called', objective.id)
    setEditingObjective(objective.id)
    const cloned = JSON.parse(JSON.stringify(objective))

    if (cloned.kpis && Array.isArray(cloned.kpis)) {
      cloned.kpis = cloned.kpis.map((kpi: KPI) => ({
        ...kpi,
        measurement_mode: inferMeasurementMode(kpi),
        measurement_direction: inferMeasurementDirection(kpi)
      }))
    }

    if (import.meta.env.DEV) console.log('Cloned objective with inferred config:', cloned)
    setOriginalObjectives(prev => ({ ...prev, [objective.id]: JSON.stringify(cloned) }))
    setEditedObjectives(prev => ({
      ...prev,
      [objective.id]: cloned
    }))
  }

  const handleCancelEdit = (objectiveId: number) => {
    setEditingObjective(null)
    setEditedObjectives(prev => {
      const next = { ...prev }
      delete next[objectiveId]
      return next
    })
  }

  const handleSaveEdit = async (objectiveId: number) => {
    if (import.meta.env.DEV) console.log('handleSaveEdit called', { objectiveId, activeTab, userRole })
    const edits = editedObjectives[objectiveId]
    if (!edits || JSON.stringify(edits) === originalObjectives[objectiveId]) {
      // Nothing changed: leave edit mode without saving, so the objective stays
      // as it is and no revision check-in is set up.
      if (import.meta.env.DEV) console.log('No edits found, exiting edit mode')
      setEditingObjective(null)
      if (edits) toast('No changes to save')
      return
    }

    if (import.meta.env.DEV) console.log('Edits found:', edits)

    if (import.meta.env.DEV) console.log(`${activeTab} tab - saving to backend`)
    setSavingEdit(objectiveId)
    try {
      await onUpdate(objectiveId, { ...edits, edited_by_role: userRole } as any, teamMember?.employee_id)

      if (activeTab === 'pending') {
        toast.success('Pending objective updated', { icon: '📝' })
      } else if (activeTab === 'rejected') {
        toast.success('Objective revised and moved to Pending Approval', {
          duration: 5000,
          icon: '📝'
        })
      } else {
        toast.success('Objective updated and moved to Pending Approval', {
          duration: 5000,
          icon: '📝'
        })
      }

      setEditingObjective(null)
      setEditedObjectives(prev => {
        const next = { ...prev }
        delete next[objectiveId]
        return next
      })

      setIsRefreshing(true)

      if (import.meta.env.DEV) console.log('Save successful, refreshing data...')

      if (teamMember) {
        try {
          const [objectivesData, sessionsData] = await Promise.all([
            performanceService.getEmployeeObjectives(teamMember.employee_id, { year: effectiveYear }),
            performanceService.getEmployeeSessions(
              teamMember.employee_id,
              { performance_year: effectiveYear }
            )
          ])

          const updatedMember = {
            ...teamMember,
            pending_objectives: objectivesData.objectives.filter((obj: any) => obj.status === 'pending_approval'),
            active_objectives: objectivesData.objectives.filter((obj: any) => obj.status === 'active'),
            rejected_objectives: objectivesData.objectives.filter((obj: any) => obj.status === 'rejected')
          }

          const isActiveStatus = (status: string) => !['completed', 'cancelled'].includes(status)
          const activeSession = sessionsData?.find((s: any) => isActiveStatus(s.status))
          const completedSession = sessionsData?.find((s: any) => s.status === 'completed')

          setFullTeamMember(updatedMember)
          setCollaborativeSession(activeSession || completedSession || null)

          if (import.meta.env.DEV) console.log('📋 Refreshed data after edit')
        } catch (err) {
          console.error('Error refreshing objectives:', err)
        }
      }

      if (onObjectivesChange) {
        onObjectivesChange().catch(err => console.error('Error refreshing parent data:', err))
      }
    } catch (error) {
      console.error('Failed to save edits:', error)
      toast.error('Failed to update objective')
    } finally {
      setSavingEdit(null)
      setIsRefreshing(false)
    }
  }

  const updateObjectiveField = (objectiveId: number, field: keyof PendingObjective, value: any) => {
    setEditedObjectives(prev => ({
      ...prev,
      [objectiveId]: {
        ...prev[objectiveId],
        [field]: value
      }
    }))
  }

  const updateKPIField = (objectiveId: number, kpiId: string, field: keyof KPI, value: any) => {
    if (import.meta.env.DEV) console.log('updateKPIField called', { objectiveId, kpiId, field, value })
    setEditedObjectives(prev => {
      const objective = prev[objectiveId]
      const updated = {
        ...prev,
        [objectiveId]: {
          ...objective,
          kpis: objective.kpis.map(kpi =>
            kpi.id === kpiId ? { ...kpi, [field]: value } : kpi
          )
        }
      }
      if (import.meta.env.DEV) console.log('Updated editedObjectives:', updated)
      return updated
    })
  }

  const handleAddKPI = (objectiveId: number) => {
    const newKPI: KPI = {
      id: `new-${Date.now()}`,
      name: '',
      description: '',
      target_value: 0,
      current_value: 0,
      unit: '',
      frequency: 'quarterly',
      category: 'performance',
      status: 'pending',
      priority: 'medium',
      start_date: null,
      end_date: null,
      measurement_direction: 'higher_is_better',
      measurement_mode: 'numeric'
    }

    setEditedObjectives(prev => ({
      ...prev,
      [objectiveId]: {
        ...prev[objectiveId],
        kpis: [...prev[objectiveId].kpis, newKPI],
        kpi_count: prev[objectiveId].kpis.length + 1
      }
    }))
  }

  const handleRemoveKPI = (objectiveId: number, kpiId: string) => {
    setEditedObjectives(prev => ({
      ...prev,
      [objectiveId]: {
        ...prev[objectiveId],
        kpis: prev[objectiveId].kpis.filter(kpi => kpi.id !== kpiId),
        kpi_count: prev[objectiveId].kpis.length - 1
      }
    }))
  }

  const resetEditState = () => {
    setEditingObjective(null)
    setEditedObjectives({})
    setSavingEdit(null)
  }

  return {
    editingObjective,
    editedObjectives,
    savingEdit,
    setEditedObjectives,
    getCurrentObjective,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    updateObjectiveField,
    updateKPIField,
    handleAddKPI,
    handleRemoveKPI,
    resetEditState
  }
}
